"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _propNameValuesToObject = _interopRequireDefault(require("../utils/propNameValuesToObject"));

var _initMode = require("../initMode");

var _PrepareValidationError = _interopRequireDefault(require("../PrepareValidationError"));

var _initSelfMode = require("../initSelfMode");

var _actionTypes = require("./actionTypes");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

/**
 * Action that initializes a single Component. Can be used both during a component prepare
 * and a component self-init.
 *
 * If the current `initMode` is `MODE_PREPARE` and the component has not yet initialized
 * when this action is dispatched, an error will be thrown.
 *
 * **There is probably no need to directly use this action outside of this module.** This
 * action is automatically dispatched by the `withInitAction()` HoC and the `prepareComponent()`
 * thunk.
 *
 * @param {react.Component} Component The React component to initialize. Passing a component that
 * was not wrapped with `withInitAction()` will result in an error.
 * @param {Array} initValues An array of values that correspond with the array of `initProps` for
 * this component. These values are either passed through `prepareComponent()` or extracted from
 * the `props` when a component mounts or updates.
 * @param {string} prepareKey A unique identifier for the component and it's `initValues`. This
 * is generated by the `getPrepareKey()` util.
 * @param {object} [options] Additional options
 * @param {string} options.caller The function from which initialization is triggered. Valid options
 * are 'prepareComponent', 'didMount', 'willMount', 'willReceiveProps'
 * @returns {function} A thunk function that should be passed directly to the Redux `dispatch`
 * function.
 */
var _default = function _default(Component, initValues, prepareKey) {
  var _ref = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
      caller = _ref.caller;

  return function (dispatch, getState) {
    if (!Component.initConfig) {
      throw new Error('No init config found on Component passed to initComponent');
    }

    var callerIsPrepare = caller === 'prepareComponent';
    var _Component$initConfig = Component.initConfig,
        componentId = _Component$initConfig.componentId,
        initProps = _Component$initConfig.initProps,
        initAction = _Component$initConfig.initAction,
        initActionClient = _Component$initConfig.initActionClient,
        initActionObjectParam = _Component$initConfig.initActionObjectParam,
        _Component$initConfig2 = _Component$initConfig.options,
        onError = _Component$initConfig2.onError,
        getInitState = _Component$initConfig2.getInitState,
        initSelf = _Component$initConfig2.initSelf,
        allowLazy = _Component$initConfig2.allowLazy;
    var initState = getInitState(getState());

    if (!initState) {
      throw new ReferenceError('Could not find init state. Did you attach the init reducer?');
    }

    var mode = initState.mode,
        prepared = initState.prepared;
    var isPrepared = typeof prepared[prepareKey] !== 'undefined';
    var errorNotPrepared;
    var shouldCallInitAction;
    var shouldCallInitActionClient;

    switch (caller) {
      case 'prepareComponent':
        errorNotPrepared = false;
        shouldCallInitAction = !!initAction && !isPrepared;
        shouldCallInitActionClient = false;
        break;

      case 'constructor':
        errorNotPrepared = !!initAction && mode === _initMode.MODE_PREPARE && !allowLazy;
        shouldCallInitAction = !!initAction && ( // mounted on the client (after first render), lazy not allowed
        mode === _initMode.MODE_INIT_SELF && initSelf !== _initSelfMode.INIT_SELF_NEVER && !allowLazy || // first render, not already prepared, lazy allowed
        mode === _initMode.MODE_PREPARE && !isPrepared && allowLazy);
        shouldCallInitActionClient = false;
        break;

      case 'didMount':
        errorNotPrepared = false;
        shouldCallInitAction = !!initAction && ( // mounted on the client (after first render), lazy allowed
        mode === _initMode.MODE_INIT_SELF && initSelf !== _initSelfMode.INIT_SELF_NEVER && allowLazy || // first render, not already prepared, lazy allowed
        mode === _initMode.MODE_PREPARE && !isPrepared && allowLazy);
        shouldCallInitActionClient = !!initActionClient && // mounted on the client (after first render)
        initSelf !== _initSelfMode.INIT_SELF_NEVER;
        break;

      case 'didUpdate':
        // reinitialize is checked in withInitAction
        errorNotPrepared = false;
        shouldCallInitAction = !!initAction;
        shouldCallInitActionClient = !!initActionClient;
        break;

      default:
        throw new Error("Unexpected value '".concat(caller, "' for caller. Expected one of: 'prepareComponent', 'didMount', 'willMount', 'willReceiveProps'"));
    }

    if (errorNotPrepared) {
      if (typeof prepared[prepareKey] === 'undefined') {
        if (initProps.length) {
          var _initPropsObj = (0, _propNameValuesToObject.default)(initProps, initValues);

          var initPropsString = JSON.stringify(_initPropsObj);
          throw new _PrepareValidationError.default("Expected component \"".concat(componentId, "\" to be prepared but prepareComponent has not been called with props: ").concat(JSON.stringify(_initPropsObj)), 'prepare-component-not-called-with-props', {
            Component: componentId,
            initPropsObject: initPropsString.length > 100 ? "".concat(initPropsString.substring(0, 100), "...") : initPropsString
          });
        } else {
          throw new _PrepareValidationError.default("Expected component \"".concat(componentId, "\" to be prepared but prepareComponent has not been called"), 'prepare-component-not-called', {
            Component: componentId
          });
        }
      } else if (prepared[prepareKey] === false) {
        var message = "Component \"".concat(componentId, "\" is preparing but preparation has not completed");

        var _initPropsObj2 = (0, _propNameValuesToObject.default)(initProps, initValues);

        var _initPropsString = '(no init props)';

        if (initProps.length) {
          message = "Component \"".concat(componentId, "\" is preparing for props ").concat(JSON.stringify(_initPropsObj2), " but preparation has not completed");
          _initPropsString = JSON.stringify(_initPropsObj2);

          if (_initPropsString.length > 100) {
            _initPropsString = "".concat(_initPropsString.substring(0, 100), "...");
          }
        }

        throw new _PrepareValidationError.default(message, 'preparation-not-completed', {
          Component: componentId,
          initPropsObject: _initPropsString
        });
      }
    }

    if (!shouldCallInitAction && !shouldCallInitActionClient) {
      return Promise.resolve();
    }

    var initPropsObj = (0, _propNameValuesToObject.default)(initProps, initValues);
    dispatch({
      type: _actionTypes.INIT_COMPONENT,
      payload: {
        complete: false,
        isPrepare: callerIsPrepare,
        prepareKey: prepareKey
      }
    });
    return Promise.resolve().then(function () {
      var initActionReturn = shouldCallInitAction ? initAction(initPropsObj, dispatch, getState) : Promise.resolve();

      if (typeof initActionReturn.then !== 'function') {
        var error = new Error("Expected initAction".concat(initActionObjectParam ? '.server' : '', " to return a Promise. Returned an ").concat(_typeof(initActionReturn), " instead. Check the initAction for \"").concat(componentId, "\""));
        error.isInvalidReturnError = true;
        throw error;
      }

      return initActionReturn;
    }).then(function (serverValue) {
      var initActionClientReturn = shouldCallInitActionClient ? initActionClient(initPropsObj, dispatch, getState) : Promise.resolve();

      if (typeof initActionClientReturn.then !== 'function') {
        var error = new Error("Expected initAction.client to return a Promise. Returned an ".concat(_typeof(initActionClientReturn), " instead. Check the initAction for \"").concat(componentId, "\""));
        error.isInvalidReturnError = true;
        throw error;
      }

      return initActionClientReturn.then(function (clientValue) {
        if (shouldCallInitAction && shouldCallInitActionClient) {
          return [serverValue, clientValue];
        }

        return shouldCallInitAction ? serverValue : clientValue;
      });
    }).catch(function (e) {
      if (onError && !e.isInvalidReturnError) {
        onError(e);
      } else {
        dispatch({
          type: _actionTypes.INIT_COMPONENT,
          payload: {
            complete: true,
            isPrepare: callerIsPrepare,
            prepareKey: prepareKey
          }
        });
        throw e;
      }
    }).then(function (result) {
      dispatch({
        type: _actionTypes.INIT_COMPONENT,
        payload: {
          complete: true,
          isPrepare: callerIsPrepare,
          prepareKey: prepareKey
        }
      });
      return result;
    });
  };
};

exports.default = _default;